import { LockIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  ChakraProvider,
  Flex,
  Grid,
  HStack,
  Tab,
  TabList,
  Tabs,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { format, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Menu } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { fallbackDate } from "../../../config/dashboardDefaults";
import { useCurrentProfile } from "../../../ContextWrapper";
import theme from "../../../theme";
import { getCustomerDashboardDate } from "../../../utils/getCustomerDashboardDate";
import LegacyDashboardLayout from "../dashboardLayout";
import ClientDashboardLayout from "../../clientDashboard/ClientDashboardLayout";
import { ReportPage } from "./reportPage";
import axios from "axios";

export const ReportNav = ({ useNewLayout = false }) => {
  const { profile } = useCurrentProfile();
  const Layout = useNewLayout ? ClientDashboardLayout : LegacyDashboardLayout;
  const [activeCustomer, setActiveCustomer] = useState(null);

  useEffect(() => {
    if (!profile) return;

    const storedId = sessionStorage.getItem("selectedCustomerId");
    const customerId =
      storedId ||
      (profile.type !== "admin" && profile.customers?.[0]?.id?.toString());
    if (!customerId) return;

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(`/api/v2/customers/${customerId}/`, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching customer:", err);
      }
    };

    fetchCustomer();
  }, [profile]);

  useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      try {
        const res = await axios.get(`/api/v2/customers/${storedId}/`, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching customer:", err);
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleSelectedChange
      );
    };
  }, [profile]);

  const customer = activeCustomer;
  const customer_type = activeCustomer?.customer_type;

  const normalizeToEOM = (date) => {
    if (!date) return fallbackDate;
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };
  const defaultDate = customer?.dashboard_date
    ? normalizeToEOM(parseISO(customer.dashboard_date))
    : normalizeToEOM(fallbackDate);

  const [initialDashDate, setInitialDashDate] = useState(() =>
    normalizeToEOM(getCustomerDashboardDate(customer))
  );
  const [startDate, setStartDate] = useState(fallbackDate);
  const [endDate, setEndDate] = useState(fallbackDate);
  const [performanceEndDate, setPerformanceEndDate] = useState(fallbackDate);
  const [performaceCategory, setPerformanceCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("last_month");

  const location = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const [tab, setTab] = useState(() => {
    if (location.pathname.includes("profit-loss")) return "profitLoss";
    if (location.pathname.includes("balance-sheet")) return "balanceSheet";
    return "performance";
  });

  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });

  useEffect(() => {
  if (!customer) return;

  const stored = localStorage.getItem("dashboardDate");

  const baseDate = stored
    ? normalizeToEOM(new Date(stored.split("-").map(Number)))
    : normalizeToEOM(getCustomerDashboardDate(customer));

  const baseDateNonPerformance = normalizeToEOM(getCustomerDashboardDate(customer));

  const lastDay = normalizeToEOM(baseDate);

  const firstDayNonPerformance = new Date(baseDateNonPerformance.getFullYear(), baseDateNonPerformance.getMonth(), 1);
  const lastDayNonPerformance = normalizeToEOM(baseDateNonPerformance);

  setInitialDashDate(baseDate);
  setStartDate(firstDayNonPerformance);
  setEndDate(lastDayNonPerformance);
  setPerformanceEndDate(lastDay);
  setPerformanceCategory(format(lastDay, "yyyy-MM"));
  setSelectedCategory("last_month");
}, [customer]);

  useEffect(() => {
    const tabFromPath = location.pathname.includes("profit-loss")
      ? "profitLoss"
      : location.pathname.includes("balance-sheet")
      ? "balanceSheet"
      : location.pathname.includes("royalty")
      ? "royalty"
      : "performance";

    setTab(tabFromPath);
  }, [location.pathname]);

  useEffect(() => {
    if (tab === "royalty" && !customer?.has_royalties) {
      setTab("performance");
      navigateRef.current("/reports/performance");
    }
  }, [customer?.has_royalties, tab]);

  return (
    <Layout>
      <ChakraProvider theme={theme}>
        <Box
          w="100%"
          h="100%"
          overflowX="auto"
          bg="#F4F4F2"
          position="relative"
          minHeight={"100vh"}
        >
          {customer_type === "trial" && (
            <Box
              position="fixed"
              top="0"
              left="0"
              w="100%"
              h="100%"
              bg="rgba(255, 255, 255, 0.7)"
              backdropFilter="blur(13px)"
              zIndex="500"
              display="flex"
              flexDir={"column"}
              alignItems="center"
              justifyContent="center"
              minHeight={"100vh"}
              maxHeight={"100%"}
            >
              <LockIcon></LockIcon>
              <Text fontSize="lg" fontWeight="800" color="gray.700" p={2}>
                Upgrade to Unlock Reports
              </Text>
            </Box>
          )}
          {isMobile && (
            <Card
              position="fixed"
              zIndex={999}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>
              </HStack>
            </Card>
          )}
          {!isMobile && <></>}
          <>
            <Card
              position={"fixed"}
              zIndex={301}
              borderRadius={0}
              mt={isMobile ? "72px" : "0px"}
              h={isMobile ? "50px" : "72px"}
              borderBottom="1px solid #e0e0e0"
              width={isMobile ? "100%" : "auto"}
              marginBottom={100}
            >
              <Tabs
                index={
                  tab === "profitLoss"
                    ? 1
                    : tab === "performance"
                    ? 0
                    : tab === "balanceSheet"
                    ? 2
                    : 3
                }
              >
                {!isMobile && (
                  <Grid
                    templateColumns="1fr 2fr 1fr 275fr"
                    width="100%"
                    alignItems="center"
                    p="0px 20px"
                    h={isMobile ? "50px" : "72px"}
                  >
                    <TabList
                      display="flex"
                      alignItems="center"
                      height={isMobile ? "50px" : "73px"}
                    >
                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("performance");
                          navigate("/reports/performance");
                        }}
                        width="170px"
                        height="73px"
                        p="0"
                        whiteSpace="nowrap"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="16px">Performance Report</Text>
                      </Tab>

                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("profitLoss");
                          navigate("/reports/profit-loss");
                        }}
                        width="90px"
                        height="73px"
                        p="0"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="16px">Profit & Loss</Text>
                      </Tab>

                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("balanceSheet");
                          navigate("/reports/balance-sheet");
                        }}
                        width="120px"
                        height="73px"
                        p="0"
                        whiteSpace="nowrap"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="16px">Balance Sheet</Text>
                      </Tab>
                      {customer?.has_royalties && (
                        <Tab
                          _focus={{
                            outline: "none",
                            border: "none",
                            boxShadow: "none",
                          }}
                          _selected={{
                            borderBottom: "3px solid #536DEC",
                            color: "#536DEC",
                            fontWeight: 700,
                          }}
                          _active={{ background: "none" }}
                          cursor="pointer"
                          onClick={() => {
                            setTab("royalty");
                            navigate("/reports/royalty");
                          }}
                          width="150px"
                          height="73px"
                          p="0"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="16px">Franchise Statement</Text>
                        </Tab>
                      )}
                    </TabList>

                    <Flex
                      gridColumn={9}
                      textAlign="right"
                      alignItems="center"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {tab === "industryInsights" && (
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          width={"200px"}
                          borderWidth={"1px"}
                          justifyContent={"center"}
                          height={"40px"}
                          borderRadius="6px"
                        >
                          <Text textAlign={"center"} fontSize={"16px"}></Text>
                        </Box>
                      )}
                    </Flex>
                  </Grid>
                )}
                {isMobile && (
                  <HStack width="100%" align="center" justify="flex-start">
                    <TabList
                      display="flex"
                      justifyContent="flex-start"
                      px={2}
                      w="100%"
                      overflowX="auto"
                      overflowY="hidden"
                      pb="1px"
                      boxShadow="inset 0 -1px 0 #E2E8F0"
                      css={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                      sx={{ "&::-webkit-scrollbar": { display: "none" } }}
                    >
                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("performance");
                          navigate("/reports/performance");
                        }}
                        h="50px"
                        p="2px 4px"
                        whiteSpace="nowrap"
                      >
                        <Text fontSize="14px" textAlign="center">
                          Performance Report
                        </Text>
                      </Tab>
                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("profitLoss");

                          navigate("/reports/profit-loss");
                        }}
                        minWidth="94px"
                        h="50px"
                        p="2px 4px"
                        whiteSpace="nowrap"
                      >
                        <Text fontSize="14px" textAlign="center">
                          Profit & Loss
                        </Text>
                      </Tab>

                      <Tab
                        _focus={{
                          outline: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                        _selected={{
                          borderBottom: "3px solid #536DEC",
                          color: "#536DEC",
                          fontWeight: 700,
                        }}
                        _active={{ background: "none" }}
                        cursor="pointer"
                        onClick={() => {
                          setTab("balanceSheet");

                          navigate("/reports/balance-sheet");
                        }}
                        minWidth="101px"
                        h="50px"
                        p="2px 4px"
                        whiteSpace="nowrap"
                      >
                        <Text fontSize="14px" textAlign="center">
                          Balance Sheet
                        </Text>
                      </Tab>
                      {customer?.has_royalties && (
                        <Tab
                          _focus={{
                            outline: "none",
                            border: "none",
                            boxShadow: "none",
                          }}
                          _selected={{
                            borderBottom: "3px solid #536DEC",
                            color: "#536DEC",
                            fontWeight: 700,
                          }}
                          _active={{ background: "none" }}
                          cursor="pointer"
                          onClick={() => {
                            setTab("royalty");

                            navigate("/reports/royalty");
                          }}
                          minWidth="150px"
                          h="50px"
                          p="2px 4px"
                          whiteSpace="nowrap"
                        >
                          <Text fontSize="14px" textAlign="center">
                            Franchise Statement
                          </Text>
                        </Tab>
                      )}
                    </TabList>
                  </HStack>
                )}
              </Tabs>
            </Card>
          </>
          {customer && tab === "profitLoss" && (
            <ReportPage
              customer={customer}
              dateFromNav={initialDashDate}
              startDate={startDate}
              endDate={endDate}
              defaultDate={defaultDate}
              performaceCategory={performaceCategory}
              setPerformanceCategory={setPerformanceCategory}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              title="Profit & Loss Statement"
              type="profitLoss"
            />
          )}
          {customer && tab === "balanceSheet" && (
            <ReportPage
              customer={customer}
              dateFromNav={initialDashDate}
              startDate={startDate}
              endDate={endDate}
              defaultDate={defaultDate}
              performaceCategory={performaceCategory}
              setPerformanceCategory={setPerformanceCategory}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              title="Balance Sheet Statement"
              type="balanceSheet"
            />
          )}
          {customer && tab === "performance" && (
            <ReportPage
              customer={customer}
              dateFromNav={initialDashDate}
              startDate={startDate}
              performanceEndDate={performanceEndDate}
              setPerformanceEndDate={setPerformanceEndDate}
              endDate={endDate}
              defaultDate={defaultDate}
              performaceCategory={performaceCategory}
              setPerformanceCategory={setPerformanceCategory}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              title="Performance Report"
              type="performance"
            />
          )}
          {customer?.has_royalties && tab === "royalty" && (
            <ReportPage
              customer={customer}
              dateFromNav={initialDashDate}
              startDate={startDate}
              performanceEndDate={performanceEndDate}
              setPerformanceEndDate={setPerformanceEndDate}
              endDate={endDate}
              defaultDate={defaultDate}
              performaceCategory={performaceCategory}
              setPerformanceCategory={setPerformanceCategory}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              title="Franchise Statement"
              type="royalty"
            />
          )}
        </Box>
      </ChakraProvider>
    </Layout>
  );
};
